'use client';

import { c as _c } from "react/compiler-runtime";
import type { ButtonState } from '@morfi/how-morfi-works';
import { useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { SimulatedTradeStage, type StageControl } from '@/components/simulated-trade/simulated-trade-stage';
import { AnimatedButton } from '@/components/ui/animated-button';
import { InfoModal } from '@/components/ui/info-modal';
import { useSimulatedTradeModalStore } from '@/stores/simulated-trade-modal';

/**
 * "View interactive explainer" — the six-stage simulated market (spec 115).
 * Store-driven and mounted once at the AppShell root, like the graduation
 * explainer; the how-it-works modals hand off to it. The stage remounts on
 * every open so the simulation always starts from the intro.
 */
export function SimulatedTradeModal() {
  const $ = _c(50);
  const t = useTranslations("simulated_trade");
  const open = useSimulatedTradeModalStore(_temp);
  const mode = useSimulatedTradeModalStore(_temp2);
  const [stage, setStage] = useState(0);
  const setOpen = useSimulatedTradeModalStore(_temp3);
  const reduceMotion = useReducedMotion();
  const controlRef = useRef(null);
  let t0;
  if ($[0] !== t) {
    t0 = () => ({
      label: t("intro_button"),
      disabled: true,
      spinner: false
    });
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const introButton = t0;
  const [button, setButton] = useState(introButton);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setButton(introButton());
    }
  }
  let t1;
  if ($[2] !== t) {
    t1 = t("stage1_name");
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t) {
    t2 = t("stage2_name");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t) {
    t3 = t("stage3_name");
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t) {
    t4 = t("stage4_name");
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t) {
    t5 = t("stage5_name");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t) {
    t6 = t("stage6_name");
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t) {
    t7 = t("stage7_name");
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t) {
    t8 = t("stage8_name");
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t) {
    t9 = t("stage9_name");
    $[18] = t;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t1 || $[21] !== t2 || $[22] !== t3 || $[23] !== t4 || $[24] !== t5 || $[25] !== t6 || $[26] !== t7 || $[27] !== t8 || $[28] !== t9) {
    t10 = [t1, t2, t3, t4, t5, t6, t7, t8, t9];
    $[20] = t1;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const stageTitles = t10;
  let t11;
  if ($[30] !== stage || $[31] !== stageTitles || $[32] !== t) {
    t11 = stage === 0 ? t("title") : stageTitles[stage - 1] ?? t("title");
    $[30] = stage;
    $[31] = stageTitles;
    $[32] = t;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => controlRef.current?.next();
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== button.disabled || $[36] !== button.label || $[37] !== button.spinner) {
    t13 = <AnimatedButton size="xl" className="w-full" disabled={button.disabled} loading={button.spinner} onClick={t12}>{button.label}</AnimatedButton>;
    $[35] = button.disabled;
    $[36] = button.label;
    $[37] = button.spinner;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== mode || $[40] !== open || $[41] !== reduceMotion || $[42] !== setOpen) {
    t14 = open ? <SimulatedTradeStage mode={mode} onStage={setStage} isStatic={reduceMotion === true} controlRef={controlRef} onButton={setButton} onClose={() => setOpen(false)} /> : null;
    $[39] = mode;
    $[40] = open;
    $[41] = reduceMotion;
    $[42] = setOpen;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== open || $[45] !== setOpen || $[46] !== t11 || $[47] !== t13 || $[48] !== t14) {
    t15 = <InfoModal open={open} onOpenChange={setOpen} size="lg" title={t11} bodyClassName="gap-0" footer={t13}>{t14}</InfoModal>;
    $[44] = open;
    $[45] = setOpen;
    $[46] = t11;
    $[47] = t13;
    $[48] = t14;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  return t15;
}
function _temp3(s_1) {
  return s_1.setOpen;
}
function _temp2(s_0) {
  return s_0.mode;
}
function _temp(s) {
  return s.open;
}